import {
  Box,
  Checkbox,
  Flex,
  Heading,
  Input,
  Spinner,
  Table,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { FiArrowLeft, FiHash } from "react-icons/fi"

import { OpenAPI } from "@/client"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import { useOrganization } from "@/contexts/OrganizationContext"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/tag-skus")({
  component: TagSkusPage,
})

interface TagRow {
  id: string
  name: string
  epcid: string
  sku?: string | null
}

function authedFetch(path: string, init: RequestInit = {}) {
  const token = localStorage.getItem("access_token")
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  })
}

function TagSkusPage() {
  const sc = useSemanticColors()
  const { t } = useTranslation("dpp")
  const navigate = useNavigate()
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  const { showToast } = useCustomToast()
  const orgId = currentOrganization?.id

  // ---- server-side search + pagination ----
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  useEffect(() => {
    const id = window.setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(0)
    }, 350)
    return () => window.clearTimeout(id)
  }, [searchInput])

  const tagsQuery = useQuery({
    queryKey: ["tag-skus", orgId, page, pageSize, search],
    queryFn: async (): Promise<{ data: TagRow[]; count: number }> => {
      const params = new URLSearchParams({
        skip: String(page * pageSize),
        limit: String(pageSize),
        sort_by: "name",
        sort_order: "asc",
      })
      if (search) params.set("search", search)
      const r = await authedFetch(
        `/api/v1/organizations/${orgId}/tags/paginated?${params.toString()}`,
      )
      if (!r.ok) throw new Error(await r.text())
      return r.json()
    },
    enabled: !!orgId,
    staleTime: 30_000,
  })

  const rows = tagsQuery.data?.data ?? []
  const totalCount = tagsQuery.data?.count ?? 0
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize))
  const rangeStart = totalCount === 0 ? 0 : page * pageSize + 1
  const rangeEnd = Math.min(totalCount, page * pageSize + rows.length)

  // SKU catalog drives the per-row pulldown — operators can only
  // assign SKUs that exist in the catalog (or unset). New SKUs are
  // created from /tag-sku-editor.
  const skuCatalogQuery = useQuery({
    queryKey: ["tag-sku-catalog", orgId],
    queryFn: async (): Promise<string[]> => {
      const r = await authedFetch(
        `/api/v1/organizations/${orgId}/tag-skus-catalog/?limit=500`,
      )
      if (!r.ok) return []
      const j = (await r.json()) as { data?: { value: string }[] }
      return (j.data ?? []).map((d) => d.value)
    },
    enabled: !!orgId,
    staleTime: 60_000,
  })
  const skuOptions = skuCatalogQuery.data ?? []

  const updateSkuMutation = useMutation({
    mutationFn: async ({ tagId, sku }: { tagId: string; sku: string }) => {
      const res = await authedFetch(`/api/v1/tags/${tagId}`, {
        method: "PUT",
        body: JSON.stringify({ sku: sku.trim() || null }),
      })
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`)
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tag-skus", orgId] })
    },
    onError: (err: unknown) => {
      showToast(
        t("tagSkus.toast.errorTitle", "Could not update SKU"),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  // ---- bulk selection + bulk apply ----
  // Selection is page-scoped — the bindings page does the same. Apply
  // hits PUT /tags/{id} once per selected tag (no backend bulk endpoint
  // yet; small N because operators pick deliberately).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkSku, setBulkSku] = useState<string>("")
  const allOnPageSelected =
    rows.length > 0 && rows.every((r) => selectedIds.has(r.id))
  const toggleRow = (id: string, next: boolean) =>
    setSelectedIds((prev) => {
      const out = new Set(prev)
      if (next) out.add(id)
      else out.delete(id)
      return out
    })
  const togglePage = (next: boolean) => {
    setSelectedIds((prev) => {
      const out = new Set(prev)
      for (const r of rows) {
        if (next) out.add(r.id)
        else out.delete(r.id)
      }
      return out
    })
  }

  const bulkApplyMutation = useMutation({
    mutationFn: async ({ ids, sku }: { ids: string[]; sku: string }) => {
      const results = await Promise.allSettled(
        ids.map((id) =>
          authedFetch(`/api/v1/tags/${id}`, {
            method: "PUT",
            body: JSON.stringify({ sku: sku.trim() || null }),
          }).then(async (res) => {
            if (!res.ok)
              throw new Error((await res.text()) || `HTTP ${res.status}`)
          }),
        ),
      )
      const failed = results.filter((r) => r.status === "rejected").length
      return { total: ids.length, failed }
    },
    onSuccess: ({ total, failed }) => {
      queryClient.invalidateQueries({ queryKey: ["tag-skus", orgId] })
      setSelectedIds(new Set())
      if (failed === 0) {
        showToast(
          t("tagSkus.toast.bulkSuccessTitle", "SKU applied"),
          t("tagSkus.toast.bulkSuccessBody", "{{count}} tag(s) updated.", {
            count: total,
          }),
          "success",
        )
      } else {
        showToast(
          t("tagSkus.toast.bulkPartialTitle", "Some updates failed"),
          t(
            "tagSkus.toast.bulkPartialBody",
            "{{ok}} of {{total}} tag(s) updated. {{failed}} failed.",
            { ok: total - failed, total, failed },
          ),
          "error",
        )
      }
    },
    onError: (err: unknown) => {
      showToast(
        t("tagSkus.toast.bulkErrorTitle", "Could not apply SKU"),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  return (
    <Box>
      <Flex mb={4} align="center" gap={3}>
        <VisionButton
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: "/admin" })}
        >
          <FiArrowLeft />
          <Text ml={2}>{t("tagSkus.backToAdmin", "← Back to admin")}</Text>
        </VisionButton>
      </Flex>

      <Flex align="center" gap={3} mb={6} flexWrap="wrap">
        <IconBox variant="glass" size="lg">
          <FiHash fontSize="24px" color={sc.iconColor} />
        </IconBox>
        <Box flex="1">
          <Heading size="lg" color={sc.heading}>
            {t("tagSkus.title", "Tag SKUs")}
          </Heading>
          <Text color={sc.textMuted}>
            {t(
              "tagSkus.subtitle",
              "Set the GS1 SKU on each tag. The DPP resolver joins Tag.sku → DPP record so tags inherit the right product data once their SKU is set.",
            )}
          </Text>
        </Box>
      </Flex>

      <VisionCard>
        <Flex
          justify="space-between"
          align="center"
          p={4}
          gap={3}
          flexWrap="wrap"
        >
          <Heading size="md" color={sc.heading}>
            {totalCount === 0
              ? t("tagSkus.empty", "No tags")
              : t(
                  "tagSkus.listTitleRange",
                  "Showing {{start}}–{{end}} of {{total}}",
                  { start: rangeStart, end: rangeEnd, total: totalCount },
                )}
          </Heading>
          <Flex gap={2} align="center" flexWrap="wrap">
            <Input
              size="sm"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t(
                "tagSkus.searchPlaceholder",
                "Search name or EPC",
              )}
              maxW="260px"
            />
          </Flex>
        </Flex>

        {selectedIds.size > 0 && (
          <Flex
            p={4}
            gap={2}
            align="center"
            flexWrap="wrap"
            borderTop="1px solid"
            borderColor={sc.border}
            bg="rgba(52,161,221,0.06)"
          >
            <Text fontSize="sm" color={sc.textMuted}>
              {t("tagSkus.bulk.label", "Apply SKU to {{count}} selected", {
                count: selectedIds.size,
              })}
            </Text>
            <select
              value={bulkSku}
              onChange={(e) => setBulkSku(e.target.value)}
              disabled={bulkApplyMutation.isPending}
              style={{
                minWidth: 220,
                padding: "6px 8px",
                borderRadius: 6,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.06)",
                color: "inherit",
                fontFamily: "monospace",
              }}
            >
              <option value="" style={{ background: "#1a202c" }}>
                {t("tagSkus.bulk.pick", "— pick a SKU —")}
              </option>
              <option value="__clear__" style={{ background: "#1a202c" }}>
                {t("tagSkus.bulk.clear", "(clear — set SKU to none)")}
              </option>
              {skuOptions.map((opt) => (
                <option key={opt} value={opt} style={{ background: "#1a202c" }}>
                  {opt}
                </option>
              ))}
            </select>
            <VisionButton
              variant="brand"
              size="sm"
              disabled={!bulkSku || bulkApplyMutation.isPending}
              onClick={() => {
                bulkApplyMutation.mutate({
                  ids: Array.from(selectedIds),
                  sku: bulkSku === "__clear__" ? "" : bulkSku,
                })
                setBulkSku("")
              }}
            >
              {t("tagSkus.bulk.apply", "Apply")}
            </VisionButton>
            <VisionButton
              variant="ghost"
              size="sm"
              disabled={bulkApplyMutation.isPending}
              onClick={() => setSelectedIds(new Set())}
            >
              {t("tagSkus.bulk.clearSelection", "Clear selection")}
            </VisionButton>
          </Flex>
        )}

        {tagsQuery.isLoading ? (
          <Flex justify="center" py={6}>
            <Spinner size="md" color={sc.spinnerColor} />
          </Flex>
        ) : rows.length === 0 ? (
          <Text color={sc.textMuted} fontSize="sm" px={4} pb={4}>
            {t("tagSkus.empty", "No tags.")}
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader w="40px">
                    <Checkbox.Root
                      checked={allOnPageSelected}
                      onCheckedChange={(d) => togglePage(!!d.checked)}
                      size="sm"
                      aria-label={t(
                        "tagSkus.selectAll",
                        "Select all on this page",
                      )}
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                    </Checkbox.Root>
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("tagSkus.col.tag", "Tag")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("tagSkus.col.epc", "EPC")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("tagSkus.col.sku", "GS1 SKU")}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((tag) => (
                  <Table.Row key={tag.id}>
                    <Table.Cell>
                      <Checkbox.Root
                        checked={selectedIds.has(tag.id)}
                        onCheckedChange={(d) => toggleRow(tag.id, !!d.checked)}
                        size="sm"
                        aria-label={`Select ${tag.name}`}
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                      </Checkbox.Root>
                    </Table.Cell>
                    <Table.Cell color={sc.heading} fontSize="sm">
                      {tag.name}
                    </Table.Cell>
                    <Table.Cell
                      color={sc.textMuted}
                      fontFamily="mono"
                      fontSize="xs"
                    >
                      {tag.epcid}
                    </Table.Cell>
                    <Table.Cell>
                      {/*
                       * Pulldown sourced from the org's SKU catalog.
                       * Includes the tag's current SKU even if it's no
                       * longer in the catalog (legacy free-typed
                       * value) so editing one of those tags doesn't
                       * silently switch it.
                       */}
                      <select
                        value={tag.sku ?? ""}
                        disabled={updateSkuMutation.isPending}
                        onChange={(e) => {
                          const next = e.target.value
                          if ((tag.sku ?? "") === next) return
                          updateSkuMutation.mutate({
                            tagId: tag.id,
                            sku: next,
                          })
                        }}
                        style={{
                          width: "100%",
                          maxWidth: 260,
                          padding: "6px 8px",
                          borderRadius: 6,
                          border: "1px solid rgba(255,255,255,0.12)",
                          background: "rgba(255,255,255,0.06)",
                          color: "inherit",
                          fontFamily: "monospace",
                        }}
                        aria-label={`SKU for ${tag.name}`}
                      >
                        <option value="" style={{ background: "#1a202c" }}>
                          {t("tagSkus.noSku", "— (none) —")}
                        </option>
                        {tag.sku && !skuOptions.includes(tag.sku) && (
                          <option
                            value={tag.sku}
                            style={{ background: "#1a202c" }}
                          >
                            {tag.sku} (legacy)
                          </option>
                        )}
                        {skuOptions.map((opt) => (
                          <option
                            key={opt}
                            value={opt}
                            style={{ background: "#1a202c" }}
                          >
                            {opt}
                          </option>
                        ))}
                      </select>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}

        {totalCount > 0 && (
          <Flex
            justify="space-between"
            align="center"
            p={4}
            gap={3}
            flexWrap="wrap"
            borderTop="1px solid"
            borderColor={sc.border}
          >
            <Flex gap={2} align="center">
              <Text fontSize="sm" color={sc.textMuted}>
                {t("tagSkus.pagination.pageSize", "Rows per page")}
              </Text>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value))
                  setPage(0)
                }}
                style={{
                  padding: "4px 6px",
                  borderRadius: 6,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.06)",
                  color: "inherit",
                }}
              >
                {[25, 50, 100, 200].map((n) => (
                  <option key={n} value={n} style={{ background: "#1a202c" }}>
                    {n}
                  </option>
                ))}
              </select>
            </Flex>
            <Flex gap={2} align="center">
              <Text fontSize="sm" color={sc.textMuted}>
                {t("tagSkus.pagination.pageOf", "Page {{page}} of {{count}}", {
                  page: page + 1,
                  count: pageCount,
                })}
              </Text>
              <VisionButton
                variant="ghost"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(0)}
              >
                « First
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                ‹ Prev
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                disabled={page >= pageCount - 1}
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              >
                Next ›
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                disabled={page >= pageCount - 1}
                onClick={() => setPage(pageCount - 1)}
              >
                Last »
              </VisionButton>
            </Flex>
          </Flex>
        )}
      </VisionCard>
    </Box>
  )
}
